import React, { useState, useRef, useLayoutEffect, useCallback } from "react";
import { Box, Stack, Typography, Collapse } from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";

import colors from "../../utils/colors";
import LayerdIcon from "./LayerdIcon";

const DESCRIPTION_MIN_MEASURE_LENGTH = 80;

const descriptionTextSx = {
  fontSize: 13,
  lineHeight: "20px",
  color: colors.neutral[700],
};

const descriptionClampSx = {
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const readMoreButtonSx = {
  mt: 0.75,
  p: 0,
  border: "none",
  background: "none",
  font: "inherit",
  fontSize: 12,
  lineHeight: "18px",
  color: colors.primary[300],
  cursor: "pointer",
  textAlign: "left",
  "&:hover": { textDecoration: "underline" },
};

function ReleaseDescription({ description, visible = true }) {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [measured, setMeasured] = useState(false);

  const measureTruncation = useCallback(() => {
    if (description.length < DESCRIPTION_MIN_MEASURE_LENGTH) {
      setIsTruncated(false);
      setMeasured(true);
      return;
    }
    if (!visible || expanded) return;

    const el = textRef.current;
    if (!el || el.clientHeight === 0) return;

    setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    setMeasured(true);
  }, [description, visible, expanded]);

  useLayoutEffect(() => {
    setMeasured(false);
    setExpanded(false);
    setIsTruncated(false);
  }, [description, visible]);

  useLayoutEffect(() => {
    if (!measured) {
      measureTruncation();
    }
  }, [measureTruncation, measured]);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(() => {
      if (!measured && visible && !expanded) {
        measureTruncation();
      } else if (measured && visible && !expanded) {
        const truncated = el.scrollHeight > el.clientHeight + 1;
        setIsTruncated(truncated);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [measureTruncation, measured, visible, expanded]);

  if (description.length < DESCRIPTION_MIN_MEASURE_LENGTH) {
    return (
      <Typography sx={{ ...descriptionTextSx, mt: 1 }}>
        {description}
      </Typography>
    );
  }

  const applyClamp = !expanded && (!measured || isTruncated);

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        ref={textRef}
        sx={{
          ...descriptionTextSx,
          ...(applyClamp ? descriptionClampSx : {}),
        }}
      >
        {description}
      </Typography>
      {isTruncated && (
        <Box
          component="button"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          sx={readMoreButtonSx}
        >
          {expanded ? "Show less" : "Read more"}
        </Box>
      )}
    </Box>
  );
}

export default function ReleaseNoteCard({ release, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box
      sx={{
        backgroundColor: colors.neutral[50],
        borderRadius: "8px",
        border: `1px solid ${colors.neutral[200]}`,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          p: 2,
          cursor: "pointer",
          "&:hover": { backgroundColor: colors.neutral[100] },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 16,
              lineHeight: "24px",
              fontWeight: 500,
              color: colors.neutral[1000],
            }}
          >
            {release.name}
          </Typography>
          <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.primary[300], mt: 0.25, fontWeight: 500 }}>
            v{release.version}
          </Typography>
        </Box>
        <LayerdIcon icon={expanded ? ChevronUp : ChevronDown} size={18} color="neutral" />
      </Stack>

      <Collapse in={expanded}>
        <Box sx={{ px: 3, pb: 3, pt: 0 }}>
          <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[600] }}>
            <Box component="span" sx={{ color: colors.neutral[500] }}>Release date: </Box>
            {release.date}
          </Typography>
          <ReleaseDescription description={release.description} visible={expanded} />
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: "21px",
              fontWeight: 500,
              color: colors.neutral[1000],
              mt: 2,
            }}
          >
            Highlights
          </Typography>
          <Stack
            component="ul"
            spacing={0.75}
            sx={{ mt: 1, pl: 3.5, m: 0, listStyleType: "disc" }}
          >
            {release.highlights.map((item) => (
              <Typography
                key={item}
                component="li"
                sx={{ fontSize: 13, lineHeight: "20px", color: colors.neutral[700] }}
              >
                {item}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}
